import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LogOut,
    Plus,
    Pencil,
    Trash2,
    Star,
    Sparkles,
    ArrowUpRight,
    Loader2,
    Search,
    Power,
    Heart,
    Megaphone,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMontures } from "@/context/MonturesContext";
import MontureForm from "@/components/admin/MontureForm";
import PopupSettings from "@/components/admin/PopupSettings";

const TABS = [
    { id: "coup-de-coeur", label: "Coup de cœur", icon: Heart, kind: "montures" },
    { id: "du-moment", label: "Du moment", icon: Sparkles, kind: "montures" },
    { id: "classiques", label: "Classiques", icon: null, kind: "montures" },
    { id: "popup", label: "Pop-up", icon: Megaphone, kind: "settings" },
];

function filterByTab(items, tab) {
    if (tab === "coup-de-coeur") return items.filter((m) => m.coupDeCoeur);
    if (tab === "du-moment")
        return items.filter((m) => m.moment && !m.coupDeCoeur);
    return items.filter((m) => !m.coupDeCoeur && !m.moment);
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const { refresh: refreshPublic } = useMontures();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [tab, setTab] = useState("coup-de-coeur");
    const [view, setView] = useState({ kind: "list" });
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await adminApi.listMontures();
            setItems(data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(
        () => ({
            "coup-de-coeur": items.filter((m) => m.coupDeCoeur).length,
            "du-moment": items.filter((m) => m.moment && !m.coupDeCoeur).length,
            classiques: items.filter((m) => !m.coupDeCoeur && !m.moment).length,
            popup: null,
        }),
        [items]
    );

    const filtered = useMemo(() => {
        if (tab === "popup") return [];
        let list = filterByTab(items, tab);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (m) =>
                    m.marque.toLowerCase().includes(q) ||
                    m.modele.toLowerCase().includes(q) ||
                    m.couleur.toLowerCase().includes(q)
            );
        }
        return list;
    }, [items, tab, search]);

    const flashToast = useCallback((msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 2500);
    }, []);

    const onCreate = useCallback(
        async (payload) => {
            setSubmitting(true);
            try {
                await adminApi.createMonture(payload);
                await load();
                refreshPublic();
                setView({ kind: "list" });
                flashToast("Monture ajoutée.");
            } catch (e) {
                alert(`Erreur : ${e.message}`);
            } finally {
                setSubmitting(false);
            }
        },
        [load, refreshPublic, flashToast]
    );

    const onUpdate = useCallback(
        async (id, payload) => {
            setSubmitting(true);
            try {
                await adminApi.updateMonture(id, payload);
                await load();
                refreshPublic();
                setView({ kind: "list" });
                flashToast("Monture mise à jour.");
            } catch (e) {
                alert(`Erreur : ${e.message}`);
            } finally {
                setSubmitting(false);
            }
        },
        [load, refreshPublic, flashToast]
    );

    const onDelete = useCallback(
        async (m) => {
            if (m.coupDeCoeur) return; // garde-fou
            if (
                !window.confirm(
                    `Supprimer définitivement « ${m.marque} ${m.modele} » ?`
                )
            )
                return;
            try {
                await adminApi.deleteMonture(m.id);
                await load();
                refreshPublic();
                flashToast("Monture supprimée.");
            } catch (e) {
                alert(`Erreur : ${e.message}`);
            }
        },
        [load, refreshPublic, flashToast]
    );

    const onToggleCoupDeCoeur = useCallback(
        async (m, next) => {
            try {
                await adminApi.updateMonture(m.id, { coupDeCoeur: next });
                await load();
                refreshPublic();
                flashToast(
                    next ? "Promue coup de cœur." : "Coup de cœur désactivé."
                );
            } catch (e) {
                alert(`Erreur : ${e.message}`);
            }
        },
        [load, refreshPublic, flashToast]
    );

    const onToggleMoment = useCallback(
        async (m, next) => {
            try {
                await adminApi.updateMonture(m.id, { moment: next });
                await load();
                refreshPublic();
                flashToast(next ? "Ajoutée au carousel." : "Retirée du carousel.");
            } catch (e) {
                alert(`Erreur : ${e.message}`);
            }
        },
        [load, refreshPublic, flashToast]
    );

    return (
        <div
            className="min-h-screen bg-lpl-bg text-lpl-text"
            data-testid="admin-dashboard"
        >
            <header className="sticky top-0 z-40 bg-lpl-bg border-b border-lpl-border">
                <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="font-display font-extrabold text-lg md:text-xl tracking-[-0.03em]"
                            data-testid="admin-logo"
                        >
                            La Petite{" "}
                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                Lunetterie
                            </span>
                            <span className="text-lpl-lavender">.</span>
                        </Link>
                        <span className="hidden md:inline-block px-3 py-1 bg-lpl-primary text-lpl-text text-[10px] uppercase tracking-[0.18em] font-bold">
                            Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/montures"
                            className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-lpl-text hover:text-lpl-primary-deep"
                            data-testid="admin-view-site"
                        >
                            Voir le site
                            <ArrowUpRight size={14} />
                        </Link>
                        <span className="hidden lg:inline text-sm text-lpl-text-muted">
                            {user?.email}
                        </span>
                        <button
                            type="button"
                            onClick={logout}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-lpl-border text-sm font-medium hover:border-lpl-text transition-colors"
                            data-testid="admin-logout"
                        >
                            <LogOut size={14} />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
                {view.kind === "list" && (
                    <>
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                            <div>
                                <p className="font-serif italic text-xl text-lpl-terracotta mb-2">
                                    {tab === "popup"
                                        ? "Communication"
                                        : "Gestion du stock"}
                                </p>
                                <h1 className="font-display font-semibold text-4xl md:text-5xl leading-[0.95] tracking-tight">
                                    {tab === "popup" ? (
                                        <>
                                            Annonce{" "}
                                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                                visiteurs
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            Mes montures{" "}
                                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                                ({items.length})
                                            </span>
                                        </>
                                    )}
                                </h1>
                            </div>
                            {tab !== "popup" && (
                                <button
                                    type="button"
                                    onClick={() => setView({ kind: "create" })}
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-lpl-primary text-lpl-text text-sm uppercase tracking-[0.16em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors"
                                    data-testid="admin-new-monture"
                                >
                                    <Plus size={16} />
                                    Ajouter une monture
                                </button>
                            )}
                        </div>

                        {/* Tabs */}
                        <div
                            className="flex flex-wrap gap-2 mb-6 border-b border-lpl-border"
                            data-testid="admin-tabs"
                            role="tablist"
                        >
                            {TABS.map((t) => {
                                const active = tab === t.id;
                                const Icon = t.icon;
                                const count = counts[t.id];
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setTab(t.id)}
                                        data-testid={`admin-tab-${t.id}`}
                                        className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                                            active
                                                ? "border-lpl-text text-lpl-text"
                                                : "border-transparent text-lpl-text-muted hover:text-lpl-text"
                                        }`}
                                    >
                                        {Icon && (
                                            <Icon
                                                size={14}
                                                fill={active ? "currentColor" : "none"}
                                            />
                                        )}
                                        {t.label}
                                        {count !== null && count !== undefined && (
                                            <span
                                                className={`ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold ${
                                                    active
                                                        ? "bg-lpl-primary text-lpl-text"
                                                        : "bg-lpl-bg-alt text-lpl-text-muted"
                                                }`}
                                            >
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {tab === "popup" ? (
                            <PopupSettings />
                        ) : (
                            <>
                                <div className="mb-6 relative max-w-md">
                                    <Search
                                        size={16}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-lpl-text-muted"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Chercher une marque, un modèle…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-11 pr-4 py-3 bg-lpl-bg-alt border-2 border-lpl-border focus:border-lpl-text outline-none text-sm"
                                        data-testid="admin-search"
                                    />
                                </div>

                                {loading ? (
                                    <div className="py-20 flex items-center justify-center text-lpl-text-muted">
                                        <Loader2 size={20} className="animate-spin mr-2" />
                                        Chargement…
                                    </div>
                                ) : error ? (
                                    <p
                                        className="px-4 py-3 bg-lpl-terracotta/15 border-l-4 border-lpl-terracotta text-lpl-terracotta"
                                        data-testid="admin-error"
                                    >
                                        {error}
                                    </p>
                                ) : filtered.length === 0 ? (
                                    <EmptyState
                                        tab={tab}
                                        onCreate={() => setView({ kind: "create" })}
                                    />
                                ) : (
                                    <div
                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                                        data-testid="admin-montures-grid"
                                    >
                                        {filtered.map((m) => (
                                            <AdminCard
                                                key={m.id}
                                                monture={m}
                                                tab={tab}
                                                onEdit={() =>
                                                    setView({ kind: "edit", monture: m })
                                                }
                                                onDelete={() => onDelete(m)}
                                                onToggleCoupDeCoeur={(v) =>
                                                    onToggleCoupDeCoeur(m, v)
                                                }
                                                onToggleMoment={(v) => onToggleMoment(m, v)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {view.kind === "create" && (
                    <FormView
                        title="Nouvelle monture"
                        onCancel={() => setView({ kind: "list" })}
                    >
                        <MontureForm
                            onCancel={() => setView({ kind: "list" })}
                            onSubmit={onCreate}
                            submitting={submitting}
                        />
                    </FormView>
                )}

                {view.kind === "edit" && (
                    <FormView
                        title={`Éditer · ${view.monture.marque} ${view.monture.modele}`}
                        onCancel={() => setView({ kind: "list" })}
                    >
                        <MontureForm
                            initial={view.monture}
                            onCancel={() => setView({ kind: "list" })}
                            onSubmit={(payload) => onUpdate(view.monture.id, payload)}
                            submitting={submitting}
                        />
                    </FormView>
                )}
            </main>

            {toast && (
                <div
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 px-5 py-3 bg-lpl-primary text-lpl-text text-sm font-medium shadow-lg z-50"
                    data-testid="admin-toast"
                >
                    {toast}
                </div>
            )}
        </div>
    );
}

function FormView({ title, children, onCancel }) {
    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-sm text-lpl-text-muted hover:text-lpl-text"
                    data-testid="admin-back"
                >
                    ← Retour
                </button>
            </div>
            <h2 className="font-display font-semibold text-3xl md:text-4xl mb-8 tracking-tight">
                {title}
            </h2>
            {children}
        </div>
    );
}

function EmptyState({ tab, onCreate }) {
    if (tab === "coup-de-coeur") {
        return (
            <div
                className="border-2 border-dashed border-lpl-border p-12 text-center"
                data-testid="empty-coup-de-coeur"
            >
                <Heart
                    size={32}
                    className="mx-auto mb-4 text-lpl-terracotta"
                />
                <p className="font-display font-semibold text-2xl mb-2">
                    Aucun coup de cœur actif
                </p>
                <p className="text-lpl-text-muted max-w-md mx-auto">
                    Le bloc « coup de cœur » est masqué sur le site public.
                    Pour le réactiver, allez dans l'onglet{" "}
                    <em>Du moment</em> ou <em>Classiques</em> et utilisez le
                    bouton cœur pour promouvoir une monture.
                </p>
            </div>
        );
    }
    return (
        <div
            className="border-2 border-dashed border-lpl-border p-12 text-center"
            data-testid={`empty-${tab}`}
        >
            <p className="font-display font-semibold text-2xl mb-2">
                Aucune monture dans cet onglet
            </p>
            <p className="text-lpl-text-muted mb-6">
                Ajoutez-en une pour commencer à remplir cette catégorie.
            </p>
            <button
                type="button"
                onClick={onCreate}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-lpl-primary text-lpl-text text-sm uppercase tracking-[0.16em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors"
            >
                <Plus size={16} />
                Ajouter une monture
            </button>
        </div>
    );
}

function AdminCard({
    monture,
    tab,
    onEdit,
    onDelete,
    onToggleCoupDeCoeur,
    onToggleMoment,
}) {
    const m = monture;
    const isCoupDeCoeur = m.coupDeCoeur;

    return (
        <article
            className="border border-lpl-border bg-lpl-bg overflow-hidden flex flex-col"
            data-testid={`admin-card-${m.id}`}
        >
            <div
                className="aspect-[4/5] flex items-center justify-center overflow-hidden relative"
                style={{ background: m.bg, color: m.fg }}
            >
                {m.image ? (
                    <img
                        src={m.image}
                        alt={`${m.marque} ${m.modele}`}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <span className="font-display text-3xl opacity-50">
                        {m.marque[0]}
                        {m.modele[0]}
                    </span>
                )}
                <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                    {isCoupDeCoeur && (
                        <Tag
                            bg="#DD7259"
                            fg="#FAF6EF"
                            icon={<Star size={10} fill="currentColor" />}
                        >
                            Coup de cœur
                        </Tag>
                    )}
                    {m.moment && (
                        <Tag bg="#0F2A20" fg="#FAF6EF" icon={<Sparkles size={10} />}>
                            Du moment
                        </Tag>
                    )}
                </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
                <p className="font-serif italic text-sm text-lpl-text-muted">
                    {m.marque}
                </p>
                <h3 className="font-display font-semibold text-xl mt-0.5 tracking-tight">
                    {m.modele}
                </h3>
                <p className="text-xs text-lpl-text-muted mt-2 capitalize">
                    {m.genre} · {m.couleur} · {m.materiau}
                </p>

                {/* Toggle bloc "Afficher sur le site" pour le coup de cœur */}
                {isCoupDeCoeur && (
                    <label
                        className="mt-4 flex items-center justify-between gap-3 px-3 py-2.5 bg-lpl-bg-alt cursor-pointer"
                        data-testid={`admin-toggle-cdc-${m.id}`}
                    >
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold">
                            <Power size={12} />
                            Bloc actif sur le site
                        </span>
                        <Switch
                            checked
                            onChange={() => onToggleCoupDeCoeur(false)}
                        />
                    </label>
                )}

                {/* Toggle "Du moment" sur les cartes hors coup de cœur */}
                {!isCoupDeCoeur && tab !== "coup-de-coeur" && (
                    <label
                        className="mt-4 flex items-center justify-between gap-3 px-3 py-2.5 bg-lpl-bg-alt cursor-pointer"
                        data-testid={`admin-toggle-moment-${m.id}`}
                    >
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold">
                            <Sparkles size={12} />
                            Dans le carousel
                        </span>
                        <Switch
                            checked={Boolean(m.moment)}
                            onChange={(v) => onToggleMoment(v)}
                        />
                    </label>
                )}

                <div className="mt-4 pt-4 border-t border-lpl-border flex gap-2">
                    <button
                        type="button"
                        onClick={onEdit}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-lpl-primary text-lpl-text text-xs uppercase tracking-[0.14em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors"
                        data-testid={`admin-edit-${m.id}`}
                    >
                        <Pencil size={12} />
                        Éditer
                    </button>

                    {!isCoupDeCoeur && (
                        <button
                            type="button"
                            onClick={() => onToggleCoupDeCoeur(true)}
                            className="inline-flex items-center justify-center px-3 py-2 border-2 border-lpl-border text-lpl-text text-xs hover:border-lpl-terracotta hover:text-lpl-terracotta transition-colors"
                            aria-label="Promouvoir en coup de cœur"
                            title="Promouvoir en coup de cœur"
                            data-testid={`admin-promote-${m.id}`}
                        >
                            <Heart size={14} />
                        </button>
                    )}

                    {!isCoupDeCoeur && (
                        <button
                            type="button"
                            onClick={onDelete}
                            className="inline-flex items-center justify-center px-3 py-2 border-2 border-lpl-border text-lpl-text text-xs hover:border-lpl-terracotta hover:text-lpl-terracotta transition-colors"
                            aria-label="Supprimer"
                            title="Supprimer"
                            data-testid={`admin-delete-${m.id}`}
                        >
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>
            </div>
        </article>
    );
}

function Tag({ bg, fg, icon, children }) {
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.16em] font-bold"
            style={{ background: bg, color: fg }}
        >
            {icon}
            {children}
        </span>
    );
}

function Switch({ checked, onChange }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 shrink-0 transition-colors ${
                checked ? "bg-lpl-primary" : "bg-lpl-border"
            }`}
        >
            <span
                className={`block h-5 w-5 bg-lpl-bg shadow translate-y-0.5 transition-transform ${
                    checked ? "translate-x-[22px]" : "translate-x-0.5"
                }`}
            />
        </button>
    );
}
